import { drawRoundRect } from "@gameApp/helpers/canvas/draw/rectRounded";
import { drawTextPure } from "@gameApp/helpers/canvas/draw/textPure";
import { getContextFont } from "@gameApp/helpers/canvas/getContextFont";
import { measureText } from "@gameApp/helpers/canvas/measureText";
import {
  BASE_FONT_SIZE,
  SMALL_FONT_SIZE,
} from "@gameComponents/MiniGame/OrderOverview/Snapshot/constants";
import colors from "tailwindcss/colors";

interface Params {
  context: CanvasRenderingContext2D;
  data: {
    title: string;
    value: string;
    priceChange?: string;
    priceChangePercent?: number;
  }[];
  columnGap: number;
  rowGap: number;
  startXY: [number, number];
}

const COLUMN_WIDTH = 168;
const GAP = 4;
const PRICE_CHANGE_RECT_VERTICAL_PADDING = 2;
const PRICE_CHANGE_RECT_HORIZONTAL_PADDING = 4;
const PRICE_CHANGE_RECT_RADIUS = 6;
const ROW_HEIGHT = 44;

const TITLE_FONT_OPTIONS = { size: BASE_FONT_SIZE, weight: 500 };
const VALUE_FONT_OPTIONS = { size: BASE_FONT_SIZE, weight: 600 };
const CHANGE_FONT_OPTIONS = { size: SMALL_FONT_SIZE, weight: 500 };

export function miniGameOrderOverviewSnapshotDrawInfoGrid({
  context,
  data,
  columnGap,
  rowGap,
  startXY,
}: Params): void {
  const [startX, startY] = startXY;

  data.forEach((item, index) => {
    const column = index % 2;
    const row = Math.floor(index / 2);

    const translateX = calculateX(startX, column, columnGap);
    const translateY = calculateY(startY, row, rowGap);

    drawTitle(context, item.title, translateX, translateY);
    const valueY = drawValue(context, item.value, translateX, translateY);

    if (item.priceChange && item.priceChangePercent) {
      drawPriceChange(
        context,
        item.value,
        item.priceChange,
        item.priceChangePercent,
        translateX,
        valueY,
      );
    }
  });
}

function calculateX(startX: number, column: number, columnGap: number): number {
  return startX + column * (COLUMN_WIDTH + columnGap);
}

function calculateY(startY: number, row: number, rowGap: number): number {
  return startY + row * (rowGap + ROW_HEIGHT);
}

function drawTitle(
  context: CanvasRenderingContext2D,
  title: string,
  x: number,
  y: number,
): void {
  drawTextPure({
    context,
    text: title,
    x,
    y,
    contextOptions: {
      font: getContextFont(TITLE_FONT_OPTIONS),
      fillStyle: colors.gray[400],
    },
  });
}

function drawValue(
  context: CanvasRenderingContext2D,
  value: string,
  x: number,
  titleY: number,
): number {
  const valueY = titleY + TITLE_FONT_OPTIONS.size + GAP;
  drawTextPure({
    context,
    text: value,
    x,
    y: valueY,
    contextOptions: {
      font: getContextFont(VALUE_FONT_OPTIONS),
      fillStyle: colors.white,
    },
  });
  return valueY;
}

function drawPriceChange(
  context: CanvasRenderingContext2D,
  value: string,
  priceChange: string,
  priceChangePercent: number,
  valueX: number,
  valueY: number,
): void {
  const measureValue = measureText(value, (ctx) => {
    ctx.font = getContextFont(VALUE_FONT_OPTIONS);
  });

  const priceChangeX = valueX + measureValue.width;
  const measurePriceChange = measureText(priceChange, (ctx) => {
    ctx.font = getContextFont(CHANGE_FONT_OPTIONS);
  });

  const rectHeight =
    CHANGE_FONT_OPTIONS.size + PRICE_CHANGE_RECT_VERTICAL_PADDING * 2;
  const rectWidth =
    measurePriceChange.width + PRICE_CHANGE_RECT_HORIZONTAL_PADDING * 2;

  const isPositiveChange = priceChangePercent > 0;
  context.fillStyle = isPositiveChange ? colors.green[950] : colors.red[950];
  drawRoundRect({
    context,
    x: priceChangeX + GAP,
    y: valueY - CHANGE_FONT_OPTIONS.size,
    width: rectWidth,
    height: rectHeight,
    radius: PRICE_CHANGE_RECT_RADIUS,
    stroke: false,
  });

  drawTextPure({
    context,
    text: priceChange,
    x: priceChangeX + GAP + PRICE_CHANGE_RECT_HORIZONTAL_PADDING,
    y: valueY,
    contextOptions: {
      font: getContextFont(CHANGE_FONT_OPTIONS),
      fillStyle: isPositiveChange ? colors.green[500] : colors.red[500],
    },
  });
}
